import { ProductCardProps } from '@design-system/product-card';
import { setLocalStorageData } from '@phx-husky/local-storage';
import { kebabCase } from 'lodash';

import TMPLinkWrap from '~/components/TMPLinkWrap';
import { headingBlockMapper } from '~/mappers/headingBlock';
import { imageMapper } from '~/mappers/image';
import {
  ItemListingProductCardCategory,
  ItemListingProductCardRef,
} from '~/mappers/itemListing/models';
import { retailersMapper } from '~/mappers/retailers';
import { traitGroupMapper } from '~/mappers/traitGroup';
import { gtmService } from '~/services/gtm';
import { GTM_LOCALSTORAGE_DATA } from '~/utils/constants';

import {
  getDescriptionProps,
  ProductCardCategory,
  ProductCardDataCommon,
  ProductCardRef,
} from './models';

const NO_DESCRIPTION = 'none';
const CMS_DESCRIPTION = 'page data';

const getDescription = ({
  descriptionField,
  cmsDescription,
  productData,
}: getDescriptionProps): string | null => {
  switch (descriptionField) {
    case NO_DESCRIPTION:
      return null;
    case CMS_DESCRIPTION:
      return cmsDescription;
    default:
      return productData[descriptionField];
  }
};

const productCardMapper = (
  {
    className,
    productData,
    listingData: { label, description: cmsDescription },
    fullPath,
    onClickProductCard,
    categories,
    listingImage,
    useFallbackImage,
    retailers: _retailers = [],
  }: ProductCardDataCommon,
  {
    imageSettings,
    cardContentUI,
    variant,
    showLabels,
    descriptionField,
    showCategory,
    buyNowButton,
    showFeatures,
  }:
    | Omit<ItemListingProductCardCategory, 'type' | 'typeName'>
    | Omit<ItemListingProductCardRef, 'type' | 'typeName'>,
  whereToBuyData
): ProductCardProps => {
  const retailers = _retailers?.[0] ?? null;

  const { heroImage, productTitle, productFeatures, barcodeNumber } =
    productData;

  const updatedWhereToBuyData = {
    ...whereToBuyData,
    barcodeNumber,
  };

  const cardImage =
    useFallbackImage && listingImage[0]
      ? imageMapper({ ...listingImage[0], ...imageSettings })
      : heroImage &&
        imageMapper({
          ...{
            alt: heroImage.alt,
            image: {
              gatsbyImageData:
                heroImage.localAsset.childImageSharp.gatsbyImageData,
              url: heroImage.url,
            },
          },
          ...imageSettings,
        });

  return {
    ...(className && {
      className,
    }),
    ...(cardImage && {
      cardMedia: {
        image: cardImage,
        mediaVariant: imageSettings.rounded ? 'rounded' : 'standard',
      },
    }),
    link: {
      component: TMPLinkWrap,
      props: {
        href: fullPath,
        onClick: onClickProductCard ? () => onClickProductCard() : undefined,
      },
    },
    cardContent: {
      headingBlock: headingBlockMapper({
        ui: cardContentUI,
        headline: productTitle,
        descriptionRte: getDescription({
          descriptionField,
          cmsDescription,
          productData,
        }),
        detail: null,
        link: [],
        button: [],
      }),
      ...(showCategory &&
        categories?.[0]?.title && {
          category: categories?.[0]?.title,
        }),
    },
    ...((showFeatures ?? true) &&
      productFeatures?.[0] && {
        traitGroup: traitGroupMapper({
          direction: 'horizontal',
          size: 'small',
          textAlign: 'start',
          withBackground: false,
          items: productFeatures.map((item) => ({
            description: item.description,
            image: { image: item.image, alt: item.alt },
          })),
        }),
      }),
    ...(showLabels &&
      label[0]?.content && {
        cardLabel: {
          labelContent: label[0].content,
          srOnlyLabelPrefix: label[0].srPrefix,
          labelVariant: label[0].variant,
          labelSize: label[0].size,
        },
      }),
    variant,
    categories: categories.map(({ title }) => kebabCase(title)),
    buyNowButton,
    ...(retailers && {
      retailers: retailersMapper(retailers, productData),
    }),
    whereToBuyData: updatedWhereToBuyData,
  } as ProductCardProps;
};

export const productCardCategoryMapper = (
  {
    productData,
    listingData,
    fullPath,
    className,
    categories,
    listingImage,
    useFallbackImage,
    buyNowButton,
    retailers,
  }: ProductCardCategory,
  settings: Omit<ItemListingProductCardCategory, 'type' | 'typeName'>,
  index,
  whereToBuyData
): ProductCardProps => {
  const onCLickProductCard = () => {
    setLocalStorageData(GTM_LOCALSTORAGE_DATA.lastProductIndex, index + 1);
    gtmService.emitSelectItem(productData, { index: index + 1 });
  };

  return productCardMapper(
    {
      productData,
      listingData: listingData || {},
      fullPath,
      className,
      onClickProductCard: onCLickProductCard,
      categories,
      listingImage,
      useFallbackImage,
      buyNowButton,
      retailers,
    },
    settings,
    whereToBuyData
  );
};

export const productCardRefMapper = (
  {
    className,
    listingData,
    fields: { fullPath, productData },
    categories,
    listingImage,
    useFallbackImage,
    buyNowButton,
    retailers,
  }: ProductCardRef,
  settings: Omit<ItemListingProductCardRef, 'type' | 'typeName'>,
  index,
  whereToBuyData
  // eslint-disable-next-line sonarjs/no-identical-functions
): ProductCardProps => {
  const onCLickProductCard = () => {
    setLocalStorageData(GTM_LOCALSTORAGE_DATA.lastProductIndex, index + 1);
    gtmService.emitSelectItem(productData, { index: index + 1 });
  };

  return productCardMapper(
    {
      className,
      productData,
      listingData: listingData || {},
      fullPath,
      onClickProductCard: onCLickProductCard,
      categories,
      listingImage,
      useFallbackImage,
      buyNowButton,
      retailers,
    },
    settings,
    whereToBuyData
  );
};
